import React, { useCallback, useEffect, useState } from "react";
import { gql, useMutation } from "@apollo/client";
import {
  addLocation,
  deleteLocation,
  listLocations,
  patchLocation,
  runBackfill,
} from "../../../api/eventLocations";
import { useAuth } from "../../../context/AuthContext";
import { ADMIN_EMAILS } from "../../../auth/adminEmails";
import ReassignOwnerModal from "./ReassignOwnerModal";
import "./LocationsTab.css";

const SET_EVENT_LOCATION_CREATED_BY = gql`
  mutation SetEventLocationCreatedBy($locationId: ID!, $createdBy: String!) {
    setEventLocationCreatedBy(locationId: $locationId, createdBy: $createdBy)
  }
`;

const formatRelative = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffMs = Date.now() - d.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 48) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  const month = Math.round(day / 30);
  if (month < 18) return `${month}mo ago`;
  const yr = Math.round(day / 365);
  return `${yr}y ago`;
};

const formatAbsolute = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

const LocationsTab = () => {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  // Sync controls
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null); // {scanned, normalized, upserted, skippedJunk}
  const [syncError, setSyncError] = useState(null);

  // Add form
  const [addTitle, setAddTitle] = useState("");
  const [addAddress, setAddAddress] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);

  // Toast for transient errors (e.g. favorite-toggle PATCH failures)
  const [toast, setToast] = useState(null); // {kind, message}
  const showToast = useCallback((kind, message) => {
    setToast({ kind, message });
    window.setTimeout(() => setToast(null), 4000);
  }, []);

  // Phase A.3 reassignment — admin-only.
  const [reassignTarget, setReassignTarget] = useState(null);
  const [setEventLocationCreatedBy] = useMutation(
    SET_EVENT_LOCATION_CREATED_BY
  );
  const { userInfo, userRole } = useAuth();
  const isAdminUser =
    (userInfo?.email &&
      ADMIN_EMAILS.includes(userInfo.email.toLowerCase())) ||
    userRole === "admin";

  const handleReassignPicked = async (newOwnerEmail) => {
    if (!reassignTarget) return;
    try {
      await setEventLocationCreatedBy({
        variables: {
          locationId: reassignTarget.locationId,
          createdBy: newOwnerEmail || "",
        },
      });
      const newCreatedBy = newOwnerEmail || null;
      setItems((prev) =>
        prev.map((it) =>
          it.locationId === reassignTarget.locationId
            ? { ...it, createdBy: newCreatedBy }
            : it
        )
      );
      setReassignTarget(null);
    } catch (err) {
      window.alert(`Reassign failed: ${err.message || err}`);
    }
  };

  // Delete modal
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const loadInitial = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await listLocations(null);
      setItems(Array.isArray(resp?.items) ? resp.items : []);
      setCursor(resp?.cursor || null);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("LocationsTab load error:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  // Auto-clear sync result after 10s.
  useEffect(() => {
    if (!syncResult) return undefined;
    const t = window.setTimeout(() => setSyncResult(null), 10000);
    return () => window.clearTimeout(t);
  }, [syncResult]);

  const loadMore = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const resp = await listLocations(cursor);
      const more = Array.isArray(resp?.items) ? resp.items : [];
      setItems((prev) => [...prev, ...more]);
      setCursor(resp?.cursor || null);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("LocationsTab loadMore error:", err);
      setError(err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncError(null);
    setSyncResult(null);
    try {
      const r = await runBackfill();
      setSyncResult(r);
      // Refresh table after sync.
      await loadInitial();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("LocationsTab sync error:", err);
      setSyncError(err && err.message ? err.message : "Sync failed.");
    } finally {
      setSyncing(false);
    }
  };

  const handleAdd = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setAddError(null);
    const title = (addTitle || "").trim();
    const address = (addAddress || "").trim();
    if (!title) {
      setAddError("Title is required.");
      return;
    }
    setAdding(true);
    try {
      const resp = await addLocation({ title, address });
      if (resp && resp.item) {
        setItems((prev) => [resp.item, ...prev]);
      }
      setAddTitle("");
      setAddAddress("");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("LocationsTab add error:", err);
      setAddError(err && err.message ? err.message : "Failed to add location.");
    } finally {
      setAdding(false);
    }
  };

  const toggleFavorite = async (item) => {
    const id = item.locationId;
    const next = !item.favorite;
    const prev = items;
    // Optimistic flip
    setItems((curr) =>
      curr.map((it) =>
        it.locationId === id ? { ...it, favorite: next } : it
      )
    );
    try {
      const updated = await patchLocation(id, { favorite: next });
      if (updated) {
        setItems((curr) =>
          curr.map((it) => (it.locationId === id ? updated : it))
        );
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Toggle favorite failed:", err);
      setItems(prev);
      showToast(
        "error",
        err && err.message ? err.message : "Could not update favorite."
      );
    }
  };

  const openDelete = (item) => {
    setDeleteTarget(item);
    setDeleteText("");
    setDeleteError("");
  };

  const closeDelete = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteText("");
    setDeleteError("");
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if ((deleteText || "").trim().toLowerCase() !== "delete") {
      setDeleteError("Type 'delete' to confirm.");
      return;
    }
    setDeleting(true);
    setDeleteError("");
    try {
      await deleteLocation(deleteTarget.locationId);
      const id = deleteTarget.locationId;
      setItems((prev) => prev.filter((it) => it.locationId !== id));
      setDeleteTarget(null);
      setDeleteText("");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Delete location failed:", err);
      setDeleteError(err && err.message ? err.message : "Delete failed.");
    } finally {
      setDeleting(false);
    }
  };

  const renderSourcePill = (source) => {
    const cls =
      source === "manual"
        ? "locations-tab__pill locations-tab__pill--manual"
        : "locations-tab__pill locations-tab__pill--synced";
    return <span className={cls}>{source || "synced"}</span>;
  };

  return (
    <div className="locations-tab">
      <div className="locations-tab__header">
        <h2>Locations</h2>
        <button
          type="button"
          className="locations-tab__btn"
          onClick={loadInitial}
          disabled={loading}
        >
          Refresh
        </button>
      </div>

      {toast && (
        <div
          className={`locations-tab__status locations-tab__status--${toast.kind}`}
        >
          {toast.message}
        </div>
      )}

      {/* Section 1: Sync controls */}
      <section className="locations-tab__section">
        <h3 className="locations-tab__section-title">Sync</h3>
        <div className="locations-tab__sync-controls">
          <button
            type="button"
            className="locations-tab__btn locations-tab__btn--primary"
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? "Running sync…" : "Run sync now"}
          </button>
          {syncResult && (
            <div className="locations-tab__status locations-tab__status--success">
              Synced {syncResult.upserted} locations from {syncResult.scanned}{" "}
              events (skipped {syncResult.skippedJunk} junk strings)
            </div>
          )}
          {syncError && (
            <div className="locations-tab__status locations-tab__status--error">
              {syncError}
            </div>
          )}
        </div>
      </section>

      {/* Section 2: Add manually */}
      <section className="locations-tab__section">
        <h3 className="locations-tab__section-title">Add location manually</h3>
        <form className="locations-tab__add-form" onSubmit={handleAdd}>
          <input
            type="text"
            className="locations-tab__input"
            placeholder="Title (e.g. Jarboe Park)"
            value={addTitle}
            onChange={(e) => setAddTitle(e.target.value)}
            disabled={adding}
          />
          <input
            type="text"
            className="locations-tab__input"
            placeholder="Address (optional)"
            value={addAddress}
            onChange={(e) => setAddAddress(e.target.value)}
            disabled={adding}
          />
          <button
            type="submit"
            className="locations-tab__btn locations-tab__btn--primary"
            disabled={adding}
          >
            {adding ? "Adding…" : "Add"}
          </button>
        </form>
        {addError && (
          <div className="locations-tab__inline-error">{addError}</div>
        )}
      </section>

      {/* Section 3: Table */}
      <section className="locations-tab__section">
        <h3 className="locations-tab__section-title">All locations</h3>

        {loading && (
          <div className="locations-tab__status locations-tab__status--info">
            Loading locations…
          </div>
        )}

        {error && !loading && (
          <div className="locations-tab__status locations-tab__status--error">
            Could not load locations: {error.message || "unknown error"}
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="locations-tab__status locations-tab__status--info">
            No locations yet.
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <div className="locations-tab__table-wrap">
            <table className="locations-tab__table">
              <thead>
                <tr>
                  <th aria-label="Favorite" className="locations-tab__star-cell">
                    ★
                  </th>
                  <th>Title</th>
                  <th>Address</th>
                  <th>Source</th>
                  <th>Events</th>
                  <th>Last Seen</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.locationId}>
                    <td className="locations-tab__star-cell">
                      <button
                        type="button"
                        className={`locations-tab__star${
                          item.favorite ? " locations-tab__star--on" : ""
                        }`}
                        onClick={() => toggleFavorite(item)}
                        aria-label={
                          item.favorite ? "Unfavorite" : "Favorite"
                        }
                        aria-pressed={!!item.favorite}
                        title={item.favorite ? "Unfavorite" : "Favorite"}
                      >
                        {item.favorite ? "★" : "☆"}
                      </button>
                    </td>
                    <td>{item.title || "—"}</td>
                    <td>{item.address || "—"}</td>
                    <td>{renderSourcePill(item.source)}</td>
                    <td>{Number(item.eventCount) || 0}</td>
                    <td title={formatAbsolute(item.lastSeenAt)}>
                      {formatRelative(item.lastSeenAt)}
                    </td>
                    <td>
                      {isAdminUser && (
                        <button
                          type="button"
                          className="locations-tab__btn"
                          onClick={() => setReassignTarget(item)}
                          title={
                            item.createdBy
                              ? `Owner: ${item.createdBy}`
                              : "No recorded owner"
                          }
                          style={{ marginRight: 6 }}
                        >
                          Reassign
                        </button>
                      )}
                      {item.source === "manual" && (
                        <button
                          type="button"
                          className="locations-tab__btn locations-tab__btn--danger"
                          onClick={() => openDelete(item)}
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {cursor && !loading && !error && (
          <div className="locations-tab__load-more">
            <button
              type="button"
              className="locations-tab__btn"
              onClick={loadMore}
              disabled={loadingMore}
            >
              {loadingMore ? "Loading…" : "Load more"}
            </button>
          </div>
        )}
      </section>

      <ReassignOwnerModal
        open={!!reassignTarget}
        onClose={() => setReassignTarget(null)}
        onSelect={handleReassignPicked}
        recordLabel="location"
        recordTitle={
          reassignTarget?.title || reassignTarget?.locationId || ""
        }
        currentOwner={reassignTarget?.createdBy || ""}
      />

      {deleteTarget && (
        <div className="locations-tab__modal-backdrop" onClick={closeDelete}>
          <div
            className="locations-tab__modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Delete location?</h3>
            <div>
              <strong>{deleteTarget.title}</strong>
              {deleteTarget.address && (
                <div style={{ marginTop: 4, color: "#374151" }}>
                  {deleteTarget.address}
                </div>
              )}
            </div>
            <div>
              Type <code>delete</code> to confirm:
            </div>
            <input
              type="text"
              className="locations-tab__modal-input"
              value={deleteText}
              onChange={(e) => setDeleteText(e.target.value)}
              disabled={deleting}
              autoFocus
            />
            {deleteError && (
              <div className="locations-tab__inline-error">{deleteError}</div>
            )}
            <div className="locations-tab__modal-actions">
              <button
                type="button"
                className="locations-tab__btn"
                onClick={closeDelete}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="locations-tab__btn locations-tab__btn--danger"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationsTab;
